import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import StaffService from '../services/StaffService';
import EmailService from '../services/EmailService';
import { buildResetToken } from '../services/PasswordService';
import { useUser } from '../store/UserContext';
import { formatDateTime } from '../utils/dateFormat';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useUser();
  const staffService = useMemo(() => new StaffService(), []);
  const emailService = useMemo(() => new EmailService(), []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('');
    const result = await signIn(email.trim(), password);
    if (result.error) {
      setStatus(result.error);
      return;
    }
    if (result.session?.passwordMustChange) {
      navigate('/profile');
      return;
    }
    navigate('/dashboard');
  };

  const handleReset = async () => {
    setStatus('');
    if (!email.trim()) {
      setStatus('Enter your login email to reset your password.');
      return;
    }
    setSendingReset(true);
    const staff = staffService
      .list()
      .find((member) => (member.loginEmail || member.email || '').toLowerCase() === email.trim().toLowerCase());
    if (!staff) {
      setStatus('No account found for that email.');
      setSendingReset(false);
      return;
    }
    const { token, tokenHash, expiresAt } = await buildResetToken();
    staffService.update({
      ...staff,
      passwordResetTokenHash: tokenHash,
      passwordResetExpiresAt: expiresAt,
      lastPasswordResetAt: new Date().toISOString()
    });
    const actionUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/reset-password?token=${token}`
      : `/reset-password?token=${token}`;
    emailService.sendTemplate({
      templateKey: 'staff.reset_password',
      to: staff.loginEmail || staff.email,
      data: {
        staff_name: staff.name,
        action_url: actionUrl,
        expires_at: formatDateTime(expiresAt),
        site_name: 'SafeguardPro'
      },
      entity: { type: 'staff', id: staff.id }
    });
    setStatus('Reset link sent. Check your inbox.');
    setSendingReset(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <Card className="p-6 w-full max-w-md space-y-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Sign in</h2>
          <p className="sp-muted">Access SafeguardPro with your staff login.</p>
        </div>
        <form className="space-y-3" onSubmit={handleSubmit}>
          <div>
            <label className="sp-label">Email</label>
            <input
              className="sp-input mt-2 w-full"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@centre.org"
            />
          </div>
          <div>
            <label className="sp-label">Password</label>
            <input
              className="sp-input mt-2 w-full"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          {status && <div className="text-xs sp-warning-text">{status}</div>}
          <Button type="submit" className="w-full">Sign in</Button>
        </form>
        <div className="text-xs sp-muted">
          <button type="button" className="sp-link" onClick={handleReset} disabled={sendingReset}>
            {sendingReset ? 'Sending reset…' : 'Forgot password?'}
          </button>
        </div>
      </Card>
    </div>
  );
}
